"use client";

import { useEffect, useMemo, useState, useCallback, useRef, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { fetchListings, toggleOfferStar, fetchRegionStates, type OfferRow } from "@/lib/queries";
import { formatCurrency, formatMultiplier, formatNumber, formatPercent, formatDate, DEAL_TYPE_LABELS } from "@/lib/format";
import { extractState, ALL_US_STATES } from "@/lib/extract-state";
import { DealTypeBadge } from "./components/DealTypeBadge";
import { TakeHomeValue } from "./components/TakeHomeValue";
import { Header } from "./components/Header";
import { MultiSelect } from "./components/MultiSelect";
import { sellerNoteOwedAtEnd } from "@/lib/seller-note";

type SortKey =
  | "take_home" | "multiplier" | "asking_price" | "last_seen" | "first_seen" | "cash_needed" | "routes"
  | "coc" | "wealth" | "wealth_multiplier";
type SourceFilter = "all" | "Route Consultant" | "FXG" | "Manual";
type DealTypeFilter = "all" | "cash_structure" | "sba_structure";
type RouteTypeFilter = "all" | "P&D" | "Linehaul";
type PrivacyFilter = "all" | "private" | "public";

const DEFAULT_SORT: SortKey = "take_home";

export default function ListingsPage() {
  return (
    <Suspense fallback={<PageShell loading />}>
      <ListingsPageInner />
    </Suspense>
  );
}

function PageShell({ loading }: { loading?: boolean }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto w-full max-w-7xl flex-1 px-6 py-8">
        {loading && (
          <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">Loading…</div>
        )}
      </main>
    </div>
  );
}

// Derived, offer-level wealth metrics -- computed live from data
// already loaded, NOT stored (per user decision). All three use the
// 100% Ownership scenario basis, matching projected_take_home's
// existing definition, so they stay comparable/consistent with it.
function computeWealthMetrics(
  projectedTakeHome: number | null,
  purchasePrice: number | null,
  totalCash: number | null,
  sellerNoteOwedEnd: number
) {
  if (projectedTakeHome == null || totalCash == null || totalCash === 0) {
    return { coc: null, totalWealth: null, wealthMultiplier: null };
  }
  const coc = projectedTakeHome / totalCash;
  // Minus only what the SBA seller note still owes at year 10 -- see
  // lib/seller-note.ts for why not the whole note.
  const totalWealth =
    purchasePrice != null ? projectedTakeHome * 10 + purchasePrice - sellerNoteOwedEnd : null;
  const wealthMultiplier = totalWealth != null ? totalWealth / totalCash : null;
  return { coc, totalWealth, wealthMultiplier };
}

// All filter/sort state is synced to the URL query string so it
// survives navigating to a listing detail page and back.
function ListingsPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [listings, setListings] = useState<OfferRow[] | null>(null);

  // "Clear all filters" removes every FILTERING param but leaves sort
  // (sort/dir) untouched, per user decision -- sort is a view
  // preference, not something "Clear" should reset. "Undo" restores
  // the exact prior URL string (not a recomputed diff), so it also
  // correctly restores sort if that happened to change in between --
  // it's a literal "go back to what was there a moment ago", not a
  // smart merge.
  const FILTER_PARAM_KEYS = [
    "q", "source", "deal", "route", "privacy", "starred", "states",
    "minCash", "maxCash", "minTakeHome", "minRoutes", "maxRoutes",
  ];
  const [lastClearedFilters, setLastClearedFilters] = useState<string | null>(null);

  const hasActiveFilters = FILTER_PARAM_KEYS.some((key) => searchParams.get(key));

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams.toString());
    setLastClearedFilters(params.toString()); // snapshot BEFORE clearing, for undo
    for (const key of FILTER_PARAM_KEYS) params.delete(key);
    // Empty the search box too, cancelling any debounced write still in
    // flight -- otherwise text typed just before clicking Clear would be
    // written back a moment later.
    pushedSearch.current = "";
    setSearchDraft("");
    router.push(`/?${params.toString()}`, { scroll: false });
  };

  const undoClearFilters = () => {
    if (lastClearedFilters === null) return;
    router.push(`/?${lastClearedFilters}`, { scroll: false });
    setLastClearedFilters(null); // one-shot -- undo doesn't chain past a single clear
  };

  // Region quick-select list for the State filter's region button --
  // configured on Settings > Region states, fetched once on mount
  // (rarely changes, and staleness here is low-stakes -- a click just
  // uses whatever list was current at page load).
  const [regionStates, setRegionStates] = useState<string[]>([]);
  useEffect(() => {
    fetchRegionStates()
      .then((rows) => setRegionStates(rows.filter((r) => r.included).map((r) => r.state)))
      .catch(() => {
        // Non-critical -- if this fails, the region button just
        // doesn't render (regionOptions stays empty), rest of the
        // filter row still works.
      });
  }, []);

  // Optimistic star toggle: updates local state immediately (no
  // waiting for a refetch), then persists to Supabase in the
  // background. Reverts on failure.
  const handleToggleStar = async (offerId: string, currentlyStarred: boolean) => {
    setListings((prev) =>
      prev
        ? prev.map((l) => ({
            ...l,
            listing_offers: l.listing_offers.map((o) =>
              o.id === offerId ? { ...o, starred: !currentlyStarred } : o
            ),
          }))
        : prev
    );
    try {
      await toggleOfferStar(offerId, !currentlyStarred);
    } catch {
      // Revert on failure
      setListings((prev) =>
        prev
          ? prev.map((l) => ({
              ...l,
              listing_offers: l.listing_offers.map((o) =>
                o.id === offerId ? { ...o, starred: currentlyStarred } : o
              ),
            }))
          : prev
      );
    }
  };
  const [error, setError] = useState<string | null>(null);

  const sortKey = (searchParams.get("sort") as SortKey) || DEFAULT_SORT;
  const sortDir = (searchParams.get("dir") as "asc" | "desc") || "desc";
  const sourceFilter = (searchParams.get("source") as SourceFilter) || "all";
  const dealTypeFilter = (searchParams.get("deal") as DealTypeFilter) || "all";
  const routeTypeFilter = (searchParams.get("route") as RouteTypeFilter) || "all";
  const privacyFilter = (searchParams.get("privacy") as PrivacyFilter) || "all";
  const starredOnly = searchParams.get("starred") === "1";
  const search = searchParams.get("q") || "";
  const minCashNeeded = searchParams.get("minCash") || "";
  const minTakeHome = searchParams.get("minTakeHome") || "";
  const maxCashNeeded = searchParams.get("maxCash") || "";
  const minRoutes = searchParams.get("minRoutes") || "";
  const maxRoutes = searchParams.get("maxRoutes") || "";
  const selectedStates = useMemo(
    () => (searchParams.get("states") ? searchParams.get("states")!.split(",") : []),
    [searchParams]
  );

  const setParam = useCallback(
    // replace = rewrite the current history entry instead of adding one.
    // Used by the search box, which writes on a debounce: one history
    // entry per keystroke made Back walk through the typing letter by
    // letter.
    (key: string, value: string | null, replace = false) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value === null || value === "" || value === "all") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
      // Any normal filter interaction invalidates the pending "Undo
      // clear" snapshot -- otherwise Undo would keep offering to
      // restore filters that are now stale/irrelevant after the user
      // has already set something new.
      setLastClearedFilters(null);
      if (replace) router.replace(`/?${params.toString()}`, { scroll: false });
      else router.push(`/?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  // ---- Search box ----
  // The box owns its text. It used to render the URL's q directly and
  // push a new URL on every keystroke; navigation is async, so the box
  // re-rendered with the PREVIOUS value before the URL caught up, and
  // fast typing dropped or reordered letters ("spokane" -> "espokane").
  // Now: the draft updates instantly and drives the filtering; the URL
  // follows ~250ms after typing stops, so links and Back still work.
  const [searchDraft, setSearchDraft] = useState(search);
  const pushedSearch = useRef(search);
  useEffect(() => {
    // q changed from OUTSIDE the box (Clear filters, Undo, Back/Forward)
    // -- adopt it. Our own debounced write comes back equal to
    // pushedSearch and is ignored, so it can't clobber letters typed
    // after it was sent.
    if (search !== pushedSearch.current) {
      pushedSearch.current = search;
      setSearchDraft(search);
    }
  }, [search]);
  useEffect(() => {
    if (searchDraft === pushedSearch.current) return;
    const t = setTimeout(() => {
      pushedSearch.current = searchDraft;
      setParam("q", searchDraft, true);
    }, 250);
    return () => clearTimeout(t);
  }, [searchDraft, setParam]);

  // Clicking a column header: same column toggles asc/desc; a
  // different column switches to it, defaulting to desc (matches the
  // prior dropdown's default direction for every metric here -- all
  // are "higher is better" or "most recent first").
  const handleSort = useCallback(
    (key: SortKey) => {
      const params = new URLSearchParams(searchParams.toString());
      if (sortKey === key) {
        params.set("dir", sortDir === "desc" ? "asc" : "desc");
      } else {
        params.set("sort", key);
        params.set("dir", "desc");
      }
      router.push(`/?${params.toString()}`, { scroll: false });
    },
    [router, searchParams, sortKey, sortDir]
  );

  useEffect(() => {
    fetchListings()
      .then(setListings)
      .catch((e) => setError(e.message ?? String(e)));
  }, []);

  // Static, ALL 50 states -- not derived from currently-loaded data,
  // since a listing for a state with no current results could appear
  // tomorrow (per user decision).

  // ONE ROW PER OFFER (not per listing) -- a listing with 3 offers
  // produces 3 independent rows here, each with its own numbers.
  // Offers from the same listing do NOT stay grouped/adjacent; they
  // sort/filter purely on their own merits, per user decision.
  const rows = useMemo(() => {
    if (!listings) return [];

    const withAllOffers = listings.flatMap((l) => {
      const state = extractState(l.location);
      return l.listing_offers.map((offer) => ({
        listing: l,
        offer,
        state,
        ...computeWealthMetrics(offer.projected_take_home, offer.purchase_price, offer.total_cash, sellerNoteOwedAtEnd(offer)),
      }));
    });

    let filtered = withAllOffers;
    if (sourceFilter !== "all") {
      filtered = filtered.filter((r) => r.listing.source === sourceFilter);
    }
    if (dealTypeFilter !== "all") {
      filtered = filtered.filter((r) => r.listing.listing_analysis?.deal_type === dealTypeFilter);
    }
    if (routeTypeFilter !== "all") {
      filtered = filtered.filter((r) => r.listing.route_type === routeTypeFilter);
    }
    if (privacyFilter !== "all") {
      const wantPrivate = privacyFilter === "private";
      filtered = filtered.filter((r) => Boolean(r.listing.is_private) === wantPrivate);
    }
    if (starredOnly) {
      filtered = filtered.filter((r) => r.offer.starred);
    }
    if (selectedStates.length > 0) {
      filtered = filtered.filter((r) => r.state && selectedStates.includes(r.state));
    }
    if (minCashNeeded.trim() || maxCashNeeded.trim()) {
      const min = minCashNeeded.trim() ? Number(minCashNeeded) : -Infinity;
      const max = maxCashNeeded.trim() ? Number(maxCashNeeded) : Infinity;
      if (!Number.isNaN(min) && !Number.isNaN(max)) {
        filtered = filtered.filter((r) => {
          const cash = r.offer.total_cash;
          return cash != null && cash >= min && cash <= max;
        });
      }
    }
    if (minTakeHome.trim()) {
      const min = Number(minTakeHome);
      if (!Number.isNaN(min)) {
        filtered = filtered.filter((r) => {
          const takeHome = r.offer.projected_take_home;
          return takeHome != null && takeHome >= min;
        });
      }
    }
    if (minRoutes.trim() || maxRoutes.trim()) {
      const min = minRoutes.trim() ? Number(minRoutes) : -Infinity;
      const max = maxRoutes.trim() ? Number(maxRoutes) : Infinity;
      filtered = filtered.filter((r) => {
        const routes = r.listing.routes_count;
        return routes != null && routes >= min && routes <= max;
      });
    }
    if (searchDraft.trim()) {
      const q = searchDraft.trim().toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.listing.title?.toLowerCase().includes(q) ||
          r.listing.location?.toLowerCase().includes(q) ||
          r.listing.uid.toLowerCase().includes(q) ||
          r.offer.name?.toLowerCase().includes(q)
      );
    }

    // Each column has a natural "default direction" (lower is better
    // for multiplier -- cheaper deals first; higher/more-recent is
    // better for everything else). sortDir flips relative to that
    // natural direction, so the first click on any header shows its
    // most useful ordering, and a second click reverses it.
    const getValue = (r: (typeof withAllOffers)[number]): number => {
      switch (sortKey) {
        case "take_home":
          return r.offer.projected_take_home ?? -Infinity;
        case "multiplier":
          return r.offer.multiplier ?? Infinity;
        case "asking_price":
          return r.listing.asking_price ?? 0;
        case "last_seen":
          return new Date(r.listing.last_seen).getTime();
        case "first_seen":
          return new Date(r.listing.first_seen).getTime();
        case "cash_needed":
          return r.offer.total_cash ?? Infinity;
        case "routes":
          return r.listing.routes_count ?? 0;
        case "coc":
          return r.coc ?? -Infinity;
        case "wealth":
          return r.totalWealth ?? -Infinity;
        case "wealth_multiplier":
          return r.wealthMultiplier ?? -Infinity;
      }
    };
    // multiplier and cash_needed naturally sort ascending (lower/
    // cheaper first) by default; every other column naturally sorts
    // descending (higher/more-recent first) by default.
    const naturalAscending = sortKey === "multiplier" || sortKey === "cash_needed";
    const directionMultiplier = (naturalAscending ? 1 : -1) * (sortDir === "desc" ? 1 : -1);

    const sorted = [...filtered].sort((a, b) => (getValue(a) - getValue(b)) * directionMultiplier);

    return sorted;
  }, [
    listings,
    sortKey,
    sortDir,
    sourceFilter,
    dealTypeFilter,
    routeTypeFilter,
    privacyFilter,
    starredOnly,
    searchDraft,
    maxCashNeeded,
    minCashNeeded,
    minTakeHome,
    minRoutes,
    maxRoutes,
    selectedStates,
  ]);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto w-full max-w-7xl flex-1 px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Offers</h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {listings ? `${rows.length} offers across ${listings.length} listings` : "Loading…"}
          </p>
        </div>

        <div
          data-filter-row
          className="mb-6 space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-1)] p-3"
        >
          {/* Row 1: search + categorical filters -- every control is
              the same 34px height (py-1.5 + text-sm), so nothing
              visually jumps between rows. */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              placeholder="Search title, location, or offer…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              className="h-9 w-56 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
            />
            <FilterSelect
              value={sourceFilter}
              onChange={(v) => setParam("source", v)}
              options={[
                { value: "all", label: "All sources" },
                { value: "Route Consultant", label: "Route Consultant" },
                { value: "FXG", label: "FXG" },
                { value: "Manual", label: "Manual" },
              ]}
            />
            <FilterSelect
              value={dealTypeFilter}
              onChange={(v) => setParam("deal", v)}
              options={[
                { value: "all", label: "All deal types" },
                { value: "sba_structure", label: DEAL_TYPE_LABELS.sba_structure },
                { value: "cash_structure", label: DEAL_TYPE_LABELS.cash_structure },
              ]}
            />
            <FilterSelect
              value={routeTypeFilter}
              onChange={(v) => setParam("route", v)}
              options={[
                { value: "all", label: "All route types" },
                { value: "P&D", label: "P&D" },
                { value: "Linehaul", label: "Linehaul" },
              ]}
            />
            <FilterSelect
              value={privacyFilter}
              onChange={(v) => setParam("privacy", v)}
              options={[
                { value: "all", label: "All listings" },
                { value: "private", label: "Private only" },
                { value: "public", label: "Public only" },
              ]}
            />
            <MultiSelect
              label="State"
              options={ALL_US_STATES}
              selected={selectedStates}
              onChange={(states) => setParam("states", states.length ? states.join(",") : null)}
              regionOptions={regionStates}
              regionLabel="Select region states"
            />
            <button
              onClick={() => setParam("starred", starredOnly ? null : "1")}
              className="flex h-9 items-center gap-1 rounded-md border px-3 text-sm"
              style={{
                borderColor: starredOnly ? "var(--color-caution)" : "var(--color-border)",
                color: starredOnly ? "var(--color-caution)" : "var(--color-text-secondary)",
                backgroundColor: "var(--color-surface-0)",
              }}
            >
              {starredOnly ? "★" : "☆"} Starred
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex h-9 items-center gap-1 rounded-md border px-3 text-sm"
                style={{
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-secondary)",
                  backgroundColor: "var(--color-surface-0)",
                }}
              >
                Clear all filters
              </button>
            )}
            {lastClearedFilters !== null && (
              <button
                onClick={undoClearFilters}
                className="flex h-9 items-center gap-1 rounded-md border px-3 text-sm"
                style={{
                  borderColor: "var(--color-accent)",
                  color: "var(--color-accent-strong)",
                  backgroundColor: "var(--color-surface-0)",
                }}
              >
                Undo clear
              </button>
            )}
          </div>

          {/* Row 2: numeric range filters -- separated visually from
              row 1 (categorical vs. numeric is a real distinction, not
              arbitrary), each label+inputs group has consistent
              spacing so the row reads as clearly delimited groups. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-[var(--color-border)] pt-3">
            <RangeFilterGroup label="Routes">
              <DebouncedNumberInput
                value={minRoutes}
                onCommit={(v) => setParam("minRoutes", v)}
                placeholder="Min"
                className="h-9 w-16 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
              />
              <span className="text-[var(--color-text-tertiary)]">–</span>
              <DebouncedNumberInput
                value={maxRoutes}
                onCommit={(v) => setParam("maxRoutes", v)}
                placeholder="Max"
                className="h-9 w-16 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
              />
            </RangeFilterGroup>

            <RangeFilterGroup label="Cash needed">
              <DebouncedNumberInput
                value={minCashNeeded}
                onCommit={(v) => setParam("minCash", v)}
                placeholder="Min"
                className="h-9 w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
              />
              <span className="text-[var(--color-text-tertiary)]">–</span>
              <DebouncedNumberInput
                value={maxCashNeeded}
                onCommit={(v) => setParam("maxCash", v)}
                placeholder="Max"
                className="h-9 w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
              />
            </RangeFilterGroup>

            <RangeFilterGroup label="Min 10-yr avg take-home">
              <DebouncedNumberInput
                value={minTakeHome}
                onCommit={(v) => setParam("minTakeHome", v)}
                placeholder="Min"
                className="h-9 w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
              />
            </RangeFilterGroup>
          </div>
        </div>

        {error && (
          <div
            className="mb-4 rounded-md border px-4 py-3 text-sm"
            style={{ borderColor: "var(--color-negative)", backgroundColor: "var(--color-negative-muted)", color: "var(--color-negative)" }}
          >
            Couldn&apos;t load offers: {error}
          </div>
        )}

        {!listings && !error && (
          <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">
            Loading offers…
          </div>
        )}

        {listings && rows.length === 0 && (
          <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">
            No offers match these filters.
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-1)] text-left text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">
                  <th className="px-2 py-3 font-medium"></th>
                  <th className="px-4 py-3 font-medium">Listing / Offer</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Deal</th>
                  <SortableHeader label="Routes" sortKeyValue="routes" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Added" sortKeyValue="first_seen" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Asking price" sortKeyValue="asking_price" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Cash needed" sortKeyValue="cash_needed" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Multiplier" sortKeyValue="multiplier" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="10-yr avg take-home" sortKeyValue="take_home" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="CoC rate" sortKeyValue="coc" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="10-yr wealth" sortKeyValue="wealth" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Wealth ×" sortKeyValue="wealth_multiplier" activeSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ listing, offer, coc, totalWealth, wealthMultiplier }) => (
                  <tr
                    key={offer.id}
                    className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface-1)]"
                  >
                    <td className="px-2 py-3">
                      <button
                        onClick={() => handleToggleStar(offer.id, offer.starred)}
                        className="text-lg leading-none"
                        style={{ color: offer.starred ? "var(--color-caution)" : "var(--color-text-tertiary)" }}
                        aria-label={offer.starred ? "Unstar" : "Star"}
                      >
                        {offer.starred ? "★" : "☆"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/listings/${encodeURIComponent(listing.uid)}?${(() => {
                          // Strip any stray "offer" params from the
                          // overview page's OWN query string before
                          // rebuilding the link -- this page never
                          // intentionally sets "offer" itself, but one
                          // could land here anyway (browser
                          // back/forward restoring a listing-page URL,
                          // or a stale link), and blindly appending
                          // searchParams.toString() after a fresh
                          // "offer=${offer.id}" would carry that stray
                          // value forward AND stack a new one on top
                          // every time any offer link was clicked --
                          // confirmed via real report: URLs
                          // accumulating a dozen-plus duplicate/stale
                          // "offer=" params after repeated navigation.
                          const params = new URLSearchParams(searchParams.toString());
                          params.delete("offer");
                          params.set("offer", offer.id);
                          return params.toString();
                        })()}`}
                        className="font-medium text-[var(--color-text-primary)] hover:text-[var(--color-accent-strong)]"
                      >
                        {listing.title || listing.uid}
                      </Link>
                      <div className="text-xs text-[var(--color-text-tertiary)]">
                        {listing.location} · {offer.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">{listing.source}</td>
                    <td className="px-4 py-3">
                      <DealTypeBadge dealType={listing.listing_analysis?.deal_type} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                      {formatNumber(listing.routes_count)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                      {formatDate(listing.first_seen)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--color-text-primary)]">
                      {formatCurrency(listing.asking_price)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                      {formatCurrency(offer.total_cash)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                      {formatMultiplier(offer.multiplier)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <TakeHomeValue value={offer.projected_take_home} compact />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                      {coc != null ? formatPercent(coc, 1) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <TakeHomeValue value={totalWealth} compact />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                      {formatMultiplier(wealthMultiplier)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function DebouncedNumberInput({
  value,
  onCommit,
  placeholder,
  className,
}: {
  value: string;
  onCommit: (v: string) => void;
  placeholder: string;
  className: string;
}) {
  // Local state for the input's OWN value while typing -- the URL
  // (and thus the `value` prop) only updates after a pause, not on
  // every keystroke. This fixes two things at once: (1) the cursor-
  // jump-to-end bug, caused by the input's value prop resetting from
  // the URL on every single keystroke, which resets cursor position
  // as a side effect of React re-controlling the input mid-edit; (2)
  // debounces the actual filter/URL update, avoiding a re-render (and
  // re-filter of the whole list) on every character typed.
  const [local, setLocal] = useState(value);

  // Keep local state in sync if the URL changes from elsewhere (e.g.
  // browser back/forward, or another control clearing filters).
  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (local !== value) onCommit(local);
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <input
      type="number"
      inputMode="numeric"
      placeholder={placeholder}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      className={className}
    />
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-3 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-accent)]"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function RangeFilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="whitespace-nowrap text-sm text-[var(--color-text-secondary)]">{label}</span>
      {children}
    </div>
  );
}

function SortableHeader({
  label,
  sortKeyValue,
  activeSortKey,
  sortDir,
  onSort,
}: {
  label: string;
  sortKeyValue: SortKey;
  activeSortKey: SortKey;
  sortDir: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  const isActive = activeSortKey === sortKeyValue;
  return (
    <th className="px-4 py-3 text-right font-medium">
      <button
        onClick={() => onSort(sortKeyValue)}
        className="inline-flex items-center gap-1 hover:text-[var(--color-text-primary)]"
        style={{ color: isActive ? "var(--color-accent-strong)" : undefined }}
      >
        {label}
        <span className="text-[10px]" style={{ opacity: isActive ? 1 : 0.3 }}>
          {isActive ? (sortDir === "desc" ? "▼" : "▲") : "▼"}
        </span>
      </button>
    </th>
  );
}
